/**
 * Stage 73.20t — HR / Finance Expense Claim Sheets review.
 *
 * Admin list view of every sheet submitted in the tenant, filter by
 * status + employee, drill-down modal with all line items + their
 * proof files visible inline, plus Approve/Reject buttons that advance
 * the approval workflow chain.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import MainLayout from '../components/MainLayout';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  Loader2, RefreshCw, FileDown, FileText, AlertTriangle,
  Check, X, Receipt, ChevronRight, Filter, ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useActiveCompany } from '../hooks/useActiveCompany';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_PILL = {
  pending: 'bg-amber-100 text-amber-700',
  in_progress: 'bg-amber-100 text-amber-700',
  approved: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-rose-100 text-rose-700',
  not_applicable: 'bg-slate-100 text-slate-600',
};

export default function ExpenseClaimsAdmin() {
  const [selectedCompany] = useActiveCompany();
  const [sheets, setSheets] = useState([]);
  const [status, setStatus] = useState('all');
  const [loading, setLoading] = useState(false);
  const [drill, setDrill] = useState(null);             // sheet drilled into
  const [drillData, setDrillData] = useState(null);
  const [decision, setDecision] = useState(null);       // { action: 'approve'|'reject', remarks }
  const [remarks, setRemarks] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    if (!selectedCompany) return;
    setLoading(true);
    try {
      const qs = new URLSearchParams({ company_id: selectedCompany });
      if (status !== 'all') qs.set('status', status);
      const res = await axios.get(`${API}/expense-claims?${qs.toString()}`, { withCredentials: true });
      setSheets(res.data || []);
    } catch (e) {
      toast.error('Load failed: ' + (e?.response?.data?.detail || e.message));
    }
    setLoading(false);
  }, [selectedCompany, status]);

  useEffect(() => { load(); }, [load]);


  const openDrill = async (sheet) => {
    setDrill(sheet);
    setDrillData(null);
    try {
      const res = await axios.get(`${API}/expense-claims/${sheet.id}`, { withCredentials: true });
      setDrillData(res.data);
    } catch (e) {
      toast.error('Drill failed: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const downloadPdf = async (sheet) => {
    try {
      const res = await axios.get(`${API}/expense-claims/${sheet.id}/pdf`, { withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `expense_claim_${sheet.id.slice(0, 8)}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('PDF failed'); }
  };

  const submitDecision = async () => {
    if (!decision || !drill) return;
    if (decision.action === 'reject' && !remarks.trim()) {
      toast.error('Remarks required when rejecting');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/expense-claims/${drill.id}/${decision.action}`,
        { remarks: remarks.trim() },
        { withCredentials: true },
      );
      if (decision.action === 'approve') {
        const finalStatus = res.data?.approval_status;
        toast.success(finalStatus === 'approved' ? 'Sheet fully approved — line items marked approved.' : 'Approved at this level — moved to next approver.');
      } else {
        toast.success('Sheet rejected. Employee must re-submit.');
      }
      setDecision(null);
      setRemarks('');
      await openDrill(drill);   // refresh
      await load();
    } catch (e) {
      toast.error('Action failed: ' + (e?.response?.data?.detail || e.message));
    }
    setSubmitting(false);
  };

  return (
    <MainLayout>
      <div className="p-6 space-y-6" data-testid="expense-claims-admin-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Receipt className="w-6 h-6 text-blue-600" />
              Expense Claim Sheets — Admin Review
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              Drill into any claim sheet to see all line items + proofs in one view. Approve or reject following the configured chain.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          </Button>
        </div>

        {/* Filter chips */}
        <div className="flex flex-wrap gap-2">
          {['all', 'pending', 'approved', 'rejected'].map(s => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              className={`px-3 py-1 rounded-full text-[11px] border ${status === s ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-700 border-slate-300'}`}
              data-testid={`exp-admin-filter-${s}`}
            >
              {s}
            </button>
          ))}
        </div>

        <Card className="p-0">
          {sheets.length === 0 ? (
            <p className="text-xs text-slate-500 italic text-center py-12">
              {loading ? 'Loading…' : 'No claim sheets in this filter.'}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-2">Employee</th>
                    <th className="text-left p-2">Linked Advance</th>
                    <th className="text-left p-2">Title / Trip</th>
                    <th className="text-right p-2">Lines</th>
                    <th className="text-right p-2">Total ₹</th>
                    <th className="text-left p-2">Approval</th>
                    <th className="text-left p-2">Submitted</th>
                    <th className="p-2 text-right">Open</th>
                  </tr>
                </thead>
                <tbody data-testid="exp-admin-table">
                  {sheets.map(s => (
                    <tr key={s.id} className="border-t border-slate-100 hover:bg-blue-50/40" data-testid={`exp-admin-row-${s.id}`}>
                      <td className="p-2">
                        <div className="font-medium text-slate-800">{s.employee_label?.name || '—'}</div>
                        <div className="text-[10px] text-slate-500 font-mono">{s.employee_label?.employee_code}</div>
                      </td>
                      <td className="p-2">
                        {s.advance_id ? (
                          <span className="font-mono text-blue-700">
                            {s.advance_id.slice(0, 8)}
                          </span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="p-2">
                        <div className="font-medium">{s.title}</div>
                        {s.trip_from && s.trip_to && <div className="text-[10px] text-slate-500">{s.trip_from} → {s.trip_to}</div>}
                        {s.needs_deviation_approval && (
                          <span className="inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 text-[10px]">
                            <AlertTriangle className="w-3 h-3" /> Has deviations
                          </span>
                        )}
                      </td>
                      <td className="p-2 text-right font-mono">{s.line_count}</td>
                      <td className="p-2 text-right font-mono font-semibold">₹{Number(s.total_amount).toLocaleString('en-IN')}</td>
                      <td className="p-2">
                        <ApprovalChainCompact sheet={s} />
                      </td>
                      <td className="p-2 text-slate-500 text-[10px]">{(s.created_at || '').slice(0, 10)}</td>
                      <td className="p-2 text-right">
                        <button
                          onClick={() => openDrill(s)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded bg-blue-50 hover:bg-blue-100 text-blue-700 text-[10px]"
                          data-testid={`exp-admin-open-${s.id}`}
                        >
                          Open <ChevronRight className="w-3 h-3" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* DRILL MODAL */}
        {drill && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-40 p-3" onClick={() => setDrill(null)}>
            <div className="bg-white rounded-xl w-full max-w-5xl max-h-[92vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
              <div className="p-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
                <div>
                  <h3 className="text-lg font-semibold">{drill.title}</h3>
                  <p className="text-[11px] text-slate-500">
                    {drill.employee_label?.name} · {drill.employee_label?.employee_code} · {drill.employee_label?.department}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => downloadPdf(drill)} data-testid="exp-admin-pdf-btn">
                    <FileDown className="w-3 h-3 mr-1" /> PDF
                  </Button>
                  <button onClick={() => setDrill(null)} className="text-slate-500 hover:text-slate-700"><X className="w-4 h-4" /></button>
                </div>
              </div>

              {!drillData ? (
                <Loader2 className="w-6 h-6 animate-spin mx-auto my-12" />
              ) : (
                <div className="p-5 space-y-4">
                  {/* Trip + totals */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-xs">
                    <Tile label="From → To" value={`${drillData.sheet.trip_from || '—'} → ${drillData.sheet.trip_to || '—'}`} />
                    <Tile label="Trip Dates" value={`${drillData.sheet.trip_start_date || '—'} → ${drillData.sheet.trip_end_date || '—'}`} />
                    <Tile label="Lines" value={drillData.sheet.line_count} />
                    <Tile label="Linked Advance" value={drillData.sheet.advance_id ? drillData.sheet.advance_id.slice(0,8) : "—"} />
                    <Tile label="Total" value={`₹${Number(drillData.sheet.total_amount).toLocaleString('en-IN')}`} highlight />
                  </div>

                  {/* Approval chain */}
                  <Card className="p-3" data-testid="exp-admin-approval-chain">
                    <h4 className="text-xs font-semibold mb-2 flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> Approval Chain</h4>
                    <ApprovalChainFull sheet={drillData.sheet} />
                  </Card>

                  {/* Line items with inline proofs */}
                  <Card className="p-3">
                    <h4 className="text-xs font-semibold mb-2">Line Items ({drillData.lines.length})</h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-[11px]">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="text-left p-2">Date</th>
                            <th className="text-left p-2">Head</th>
                            <th className="text-left p-2">Subcategory</th>
                            <th className="text-left p-2">Bill #</th>
                            <th className="text-left p-2">Description</th>
                            <th className="text-right p-2">Amount ₹</th>
                            <th className="text-left p-2">Policy</th>
                            <th className="text-left p-2">Proof</th>
                          </tr>
                        </thead>
                        <tbody data-testid="exp-admin-lines">
                          {drillData.lines.map(l => {
                            const pc = l.policy_check || {};
                            return (
                              <tr key={l.id} className="border-t border-slate-100" data-testid={`exp-admin-line-${l.id}`}>
                                <td className="p-2">{l.bill_date}</td>
                                <td className="p-2 font-medium">{l.head_code}</td>
                                <td className="p-2">{l.subcategory || '—'}</td>
                                <td className="p-2">{l.bill_number || '—'}</td>
                                <td className="p-2">{l.description || '—'}</td>
                                <td className="p-2 text-right font-mono font-semibold">{Number(l.claim_amount).toLocaleString('en-IN')}</td>
                                <td className="p-2">
                                  {pc.has_policy && (pc.needs_deviation_approval ? (
                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 text-[10px]"><AlertTriangle className="w-3 h-3" /> +₹{Math.round(pc.exceeds_by).toLocaleString('en-IN')}</span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 text-[10px]">Within</span>
                                  ))}
                                </td>
                                <td className="p-2">
                                  {l.proof_file_id ? (
                                    <a href={`${API}/kyc/proofs/${l.proof_file_id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-blue-600 hover:underline" data-testid={`exp-admin-proof-${l.id}`}>
                                      <FileText className="w-3 h-3" /> View
                                    </a>
                                  ) : <span className="text-slate-400 text-[10px]">—</span>}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </Card>

                  {/* Decision bar */}
                  {drillData.sheet.approval_status === 'in_progress' && (
                    <div className="sticky bottom-0 bg-white border-t border-slate-200 p-3 -mx-5 -mb-5">
                      <p className="text-[11px] text-slate-600 mb-2">
                        Awaiting approval at level <b>{drillData.sheet.approvals?.[drillData.sheet.current_level_index]?.level_name || '?'}</b>.
                        Only the configured approver for this level (or a tenant admin) can act.
                      </p>
                      <div className="flex gap-2 justify-end">
                        <Button
                          size="sm"
                          onClick={() => { setDecision({ action: 'reject' }); setRemarks(''); }}
                          className="bg-rose-100 hover:bg-rose-200 text-rose-700 border-rose-200"
                          variant="outline"
                          data-testid="exp-admin-reject-btn"
                        >
                          <X className="w-3 h-3 mr-1" /> Reject Sheet
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => { setDecision({ action: 'approve' }); setRemarks(''); }}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          data-testid="exp-admin-approve-btn"
                        >
                          <Check className="w-3 h-3 mr-1" /> Approve this Level
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Decision sub-modal */}
        {decision && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setDecision(null)}>
            <div className="bg-white rounded-xl p-5 max-w-md w-full shadow-2xl" onClick={e => e.stopPropagation()} data-testid="exp-admin-decision-modal">
              <h3 className="text-lg font-semibold mb-2">
                {decision.action === 'approve' ? 'Approve this level' : 'Reject sheet'}
              </h3>
              <p className="text-xs text-slate-500 mb-3">{drill?.title}</p>
              <label className="text-xs font-medium">Remarks {decision.action === 'reject' && <span className="text-rose-600">*</span>}</label>
              <textarea
                value={remarks} onChange={e => setRemarks(e.target.value)} rows={3}
                placeholder={decision.action === 'approve' ? 'Optional — e.g. "All bills attached"' : 'Required — why is this being rejected?'}
                className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm mt-1"
                data-testid="exp-admin-remarks"
              />
              <div className="flex gap-2 mt-3">
                <Button variant="outline" size="sm" onClick={() => setDecision(null)} className="flex-1">Cancel</Button>
                <Button
                  size="sm" onClick={submitDecision} disabled={submitting}
                  className={`flex-1 ${decision.action === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-rose-600 hover:bg-rose-700'} text-white`}
                  data-testid="exp-admin-confirm-decision"
                >
                  {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : (decision.action === 'approve' ? 'Approve' : 'Reject')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}

function Tile({ label, value, highlight }) {
  return (
    <div className={`rounded p-2 ${highlight ? 'bg-emerald-50 border border-emerald-200' : 'bg-slate-50 border border-slate-200'}`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`font-semibold mt-0.5 ${highlight ? 'text-emerald-700 text-base' : 'text-slate-800'}`}>{value}</div>
    </div>
  );
}

function ApprovalChainCompact({ sheet }) {
  const approvals = sheet.approvals || [];
  if (!approvals.length) {
    return <span className="text-[10px] text-slate-400 italic">No workflow</span>;
  }
  return (
    <div className="flex flex-wrap items-center gap-1">
      {approvals.map((a, i) => (
        <React.Fragment key={i}>
          <span className={`text-[9px] px-1.5 py-0.5 rounded ${
            a.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
            a.status === 'rejected' ? 'bg-rose-100 text-rose-700' :
            i === sheet.current_level_index ? 'bg-amber-100 text-amber-700 font-semibold' :
            'bg-slate-100 text-slate-500'
          }`}>{a.level_name}</span>
          {i < approvals.length - 1 && <span className="text-slate-300 text-[9px]">›</span>}
        </React.Fragment>
      ))}
      <span className={`ml-1 text-[9px] px-1.5 py-0.5 rounded ${STATUS_PILL[sheet.approval_status]}`}>{sheet.approval_status?.replace('_', ' ')}</span>
    </div>
  );
}

function ApprovalChainFull({ sheet }) {
  const approvals = sheet.approvals || [];
  if (!approvals.length) {
    return <p className="text-[11px] text-slate-500 italic">No approval workflow attached. Goes direct to finance.</p>;
  }
  return (
    <div className="space-y-1.5">
      {approvals.map((a, i) => (
        <div
          key={i}
          className={`flex items-center justify-between border rounded p-2 text-xs ${
            a.status === 'approved' ? 'border-emerald-200 bg-emerald-50' :
            a.status === 'rejected' ? 'border-rose-200 bg-rose-50' :
            i === sheet.current_level_index ? 'border-amber-300 bg-amber-50' :
            'border-slate-200 bg-white'
          }`}
        >
          <div>
            <span className="font-semibold">#{i + 1} {a.level_name}</span>
            <span className="text-[10px] text-slate-500 ml-2">
              {a.approver_role && `Role: ${a.approver_role}`}
              {a.approver_user_ids?.length > 0 && ` · ${a.approver_user_ids.length} named approver(s)`}
            </span>
          </div>
          <div className="text-right">
            <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase ${
              a.status === 'approved' ? 'bg-emerald-200 text-emerald-800' :
              a.status === 'rejected' ? 'bg-rose-200 text-rose-800' :
              i === sheet.current_level_index ? 'bg-amber-200 text-amber-800' :
              'bg-slate-200 text-slate-600'
            }`}>{a.status}</span>
            {a.approver_name && (
              <div className="text-[10px] text-slate-500 mt-0.5">
                by <b>{a.approver_name}</b>
                {a.decided_at && ` · ${(a.decided_at || '').slice(0, 10)}`}
              </div>
            )}
            {a.remarks && <div className="text-[10px] italic text-slate-600">"{a.remarks}"</div>}
          </div>
        </div>
      ))}
    </div>
  );
}
